import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
	fetchGrillesPolice,
	postGrillePolice,
	deleteGrillePolice,
	resetPostGrillePolice,
} from "../../../store/actions/policeTarifsActions"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import { useGroups } from "../../../hooks/groups"
import CustomModal from "../../basics/interface/CustomModal"
import Loader from "../../basics/Loader"
import { useQuitGuard } from "../../../hooks/useQuitGuard"

// Sous-onglet « Tarifs police » (GE-8) — grille officielle versionnée,
// calquée sur l'annexe (art. 2 al. 2). Maquette de référence :
// SvpMobile/docs/mockup-tarifs-police-config.html. Les codes prestation /
// zones / moments et l'encodage catégorie MIRRORENT svp/police_tarifs.py
// (catégorie = VEHICULE_CATEGORIE de Utils.js) — ne rien renuméroter ici.

const ETAT_BROUILLON = 0
const ETAT_EN_VIGUEUR = 1
const ETAT_ARCHIVEE = 2

// Colonnes : zone × catégorie (l'ordre de l'annexe officielle).
const CATS = [
	{ zone: "normale", categorie: 4, label: "Vélos, cargos & cyclos *" },
	{ zone: "normale", categorie: 3, label: "Motos, scooters & quads" },
	{ zone: "normale", categorie: 1, label: "Voitures ≤ 3,5 t" },
	{ zone: "normale", categorie: 2, label: "Lourds > 3,5 t ***" },
	{ zone: "autoroute", categorie: 3, label: "Motos, scooters & quads" },
	{ zone: "autoroute", categorie: 1, label: "Voitures ≤ 3,5 t" },
	{ zone: "autoroute", categorie: 2, label: "Lourds > 3,5 t ***" },
]

// Lignes : sections + prestations. moment: "JN" | "J" | "N" | "JOUR".
const SECTIONS = [
	{
		titre: "1. Mise à disposition",
		rows: [
			{ code: "1.1", label: "Lieu d'intervention à dépôt", moment: "JN" },
			{ code: "1.2", label: "Course à vide", moment: "JN" },
			{
				code: "1.3",
				label: "Véhicule chargé restitué sur place",
				moment: "JN",
			},
			{
				code: "1.4",
				label: "Transport dépôt – fourrière",
				note: "hors saisie terrain (mission de transfert)",
				moment: "J",
			},
			{ code: "1.5", label: "Véhicule déplacé", moment: "JN" },
		],
	},
	{
		titre: "2. Véhicule accidenté",
		rows: [
			{
				code: "2.1",
				label: "Lieu d'intervention à dépôt",
				note: "y compris nettoyage chaussée",
				moment: "JN",
			},
		],
	},
	{
		titre: "3. Cas particuliers",
		rows: [
			{
				code: "3.1",
				label: "Frais de garde voitures / camions",
				note: "par jour dès la 13ᵉ h ** · gardiennage au dépôt — zone indifférente",
				moment: "JOUR",
			},
			{
				code: "3.2",
				label: "Frais de garde deux-roues, tricycles, quads",
				note: "par jour dès la 13ᵉ h ** · gardiennage au dépôt — zone indifférente",
				moment: "JOUR",
			},
			{
				code: "3.3",
				label: "Restitution tout véhicule",
				note: "18h30 – 07h00",
				moment: "N",
			},
			{
				code: "3.4",
				label: "Assistant supplémentaire",
				note: "par tranche de ½ h et par assistant",
				moment: "JN",
			},
			{
				code: "3.5",
				label: "Intervention spécifique",
				note: "haute tension, treuillage, sous-sol…",
				moment: "JN",
			},
			{
				code: "3.6",
				label: "Supplément horaire dès la ½ h",
				note: "par tranche de ½ h",
				moment: "JN",
			},
		],
	},
	{
		titre: "4. Suppléments",
		rows: [
			{
				code: "S1",
				label: "Véhicules > 12 t",
				note: "s'ajoute aux positions 1 et 2",
				moment: "JN",
			},
		],
	},
]

const momentsOf = (row) => {
	if (row.moment === "JN") return ["J", "N"]
	if (row.moment === "J") return ["J"]
	if (row.moment === "N") return ["N"]
	return ["JOUR"]
}

const cellKey = (code, zone, categorie, moment) =>
	`${code}|${zone}|${categorie}|${moment}`

const cellsFromGrille = (grille) => {
	const map = {}
	;(grille?.cellules || []).forEach((c) => {
		map[cellKey(c.prestation, c.zone, c.categorie, c.moment)] = c.prix
	})
	return map
}

const dateFr = (iso) => {
	if (!iso) return ""
	const [y, m, d] = iso.split("-")
	return `${d}.${m}.${y}`
}

const styles = {
	card: {
		background: COLORS.surface,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 14,
		overflow: "hidden",
	},
	head: { padding: "18px 20px 12px" },
	title: { fontSize: 15, fontWeight: 600, color: COLORS.text },
	sub: { fontSize: 13, color: COLORS.textMuted, marginTop: 3, maxWidth: 680 },
	rail: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		flexWrap: "wrap",
		padding: "0 20px 14px",
		borderBottom: `1px solid ${COLORS.border}`,
	},
	railLabel: {
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: "0.08em",
		textTransform: "uppercase",
		color: COLORS.textLight,
		marginRight: 4,
	},
	pill: (selected, brouillon) => ({
		display: "inline-flex",
		alignItems: "center",
		gap: 7,
		fontSize: 13,
		cursor: "pointer",
		border: `1px ${brouillon ? "dashed" : "solid"} ${
			selected ? COLORS.accent : COLORS.border
		}`,
		borderRadius: 99,
		background: selected ? COLORS.accentLight : COLORS.surface,
		color: selected ? COLORS.accent : COLORS.textLight,
		fontWeight: selected ? 600 : 500,
		padding: "5px 13px",
	}),
	dot: (color) => ({
		width: 8,
		height: 8,
		borderRadius: 99,
		background: color,
	}),
	toolbar: {
		display: "flex",
		alignItems: "center",
		gap: 16,
		flexWrap: "wrap",
		padding: "12px 20px",
		fontSize: 12.5,
		color: COLORS.textMuted,
	},
	scroll: { overflowX: "auto", borderTop: `1px solid ${COLORS.border}` },
	table: {
		borderCollapse: "separate",
		borderSpacing: 0,
		minWidth: 1080,
		width: "100%",
		fontVariantNumeric: "tabular-nums",
	},
	zoneHead: {
		fontSize: 11,
		letterSpacing: "0.09em",
		textTransform: "uppercase",
		color: COLORS.text,
		background: COLORS.bg,
		borderBottom: `1px solid ${COLORS.border}`,
		padding: "8px 6px",
		textAlign: "center",
	},
	catHead: {
		fontWeight: 600,
		color: COLORS.text,
		fontSize: 12,
		padding: "7px 8px",
		background: COLORS.bg,
		borderBottom: `1px solid ${COLORS.border}`,
		textAlign: "center",
		lineHeight: 1.25,
	},
	momentHead: {
		width: 56,
		color: COLORS.textLight,
		fontSize: 11,
		background: COLORS.bg,
		borderBottom: `1px solid ${COLORS.border}`,
		padding: "5px 4px",
		textAlign: "center",
	},
	stickyCol: {
		position: "sticky",
		left: 0,
		zIndex: 2,
		background: COLORS.surface,
		textAlign: "left",
		minWidth: 240,
		maxWidth: 300,
		borderRight: `1px solid ${COLORS.border}`,
		borderBottom: `1px solid ${COLORS.border}`,
		padding: "7px 12px",
	},
	sectionRow: {
		background: COLORS.bg,
		color: COLORS.text,
		fontSize: 11.5,
		fontWeight: 700,
		letterSpacing: "0.05em",
		textTransform: "uppercase",
		padding: "6px 12px",
		borderBottom: `1px solid ${COLORS.border}`,
	},
	code: {
		display: "inline-block",
		minWidth: 28,
		textAlign: "center",
		fontSize: 11,
		fontWeight: 700,
		color: COLORS.accent,
		background: COLORS.accentLight,
		borderRadius: 6,
		padding: "1px 5px",
		marginRight: 8,
	},
	prestaLabel: { fontSize: 12.5, fontWeight: 500, color: COLORS.text },
	prestaNote: {
		display: "block",
		fontSize: 11,
		color: COLORS.textLight,
		marginTop: 1,
		marginLeft: 36,
	},
	cell: (diff, zoneStart) => ({
		textAlign: "center",
		padding: "2px 2px",
		minWidth: 54,
		borderBottom: `1px solid ${COLORS.border}`,
		borderLeft: zoneStart ? `2px solid ${COLORS.borderStrong}` : undefined,
		background: diff ? "#FFF7E6" : undefined,
	}),
	cellInput: (diff) => ({
		font: "inherit",
		fontSize: 13,
		width: 52,
		textAlign: "center",
		border: `1px solid ${diff ? "#F2D9A6" : "transparent"}`,
		borderRadius: 7,
		background: diff ? "#FFF7E6" : "transparent",
		color: COLORS.text,
		fontWeight: diff ? 600 : 400,
		padding: "4px 2px",
	}),
	cellValue: { fontSize: 13, color: COLORS.text, padding: "4px 2px" },
	na: { color: COLORS.textLight },
	footnotes: {
		padding: "12px 20px 16px",
		fontSize: 12,
		color: COLORS.textMuted,
		display: "grid",
		gap: 3,
	},
	savebar: {
		display: "flex",
		alignItems: "center",
		gap: 12,
		padding: "12px 20px",
		borderTop: `1px solid ${COLORS.border}`,
		background: COLORS.bg,
	},
	dateInput: {
		font: "inherit",
		fontSize: 13,
		color: COLORS.text,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 8,
		padding: "3px 7px",
		background: COLORS.surface,
	},
}

const TarifsPoliceV2 = () => {
	const dispatch = useDispatch()
	const { isBoss } = useGroups()
	const { grilles, fetchGrilles, postGrille } = useSelector(
		(s) => s.policeTarifsInfo,
	)

	const brouillon = grilles.find((g) => g.etat === ETAT_BROUILLON)
	const enVigueur = grilles.find((g) => g.etat === ETAT_EN_VIGUEUR)

	const [selectedId, setSelectedId] = useState(null)
	const [draft, setDraft] = useState({})
	const [draftDate, setDraftDate] = useState("")
	const [dirty, setDirty] = useState(false)
	const [autofillN, setAutofillN] = useState(true)
	// Modal de confirmation : null | "sauver" | "vigueur" | "supprimer".
	const [confirm, setConfirm] = useState(null)

	useEffect(() => {
		dispatch(fetchGrillesPolice())
	}, [dispatch])

	// GE-66 J6 : saisie non enregistrée → garde de sortie (navigation,
	// fermeture d'onglet, rechargement au deploy).
	const {
		setDirty: garderSaisie,
		setClean: libererSaisie,
		QuitGuard,
	} = useQuitGuard()
	useEffect(() => {
		if (dirty) garderSaisie()
		else libererSaisie()
	}, [dirty, garderSaisie, libererSaisie])

	// Sélection par défaut : le brouillon s'il existe, sinon la version en
	// vigueur ; re-synchronise le draft quand la liste fraîche arrive (sauf
	// modifications locales non sauvées).
	useEffect(() => {
		if (!grilles.length) return
		const cible =
			grilles.find((g) => g.id === selectedId) || brouillon || enVigueur
		if (!cible) return
		if (cible.id !== selectedId) setSelectedId(cible.id)
		if (cible.etat === ETAT_BROUILLON && !dirty) {
			setDraft(cellsFromGrille(cible))
			setDraftDate(cible.valable_des)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [grilles])

	const selected = grilles.find((g) => g.id === selectedId)
	const editable = isBoss && selected?.etat === ETAT_BROUILLON
	const baseCells = useMemo(() => cellsFromGrille(enVigueur), [enVigueur])
	const shownCells = editable ? draft : cellsFromGrille(selected)

	const diffKeys = useMemo(() => {
		if (!editable) return []
		const keys = new Set([...Object.keys(baseCells), ...Object.keys(draft)])
		return [...keys].filter(
			(k) => (draft[k] ?? null) !== (baseCells[k] ?? null),
		)
	}, [editable, draft, baseCells])

	// GE-66 K10 (Jordan 13.10) : changer de pastille ne touche jamais à la
	// saisie (un seul brouillon, déjà synchronisé) ni à la garde de sortie.
	const selectVersion = (g) => setSelectedId(g.id)

	const setCell = (key, raw) => {
		const clean = raw.trim().replace(",", ".")
		const value = clean === "" ? null : Number(clean)
		setDraft((d) => {
			const next = { ...d }
			if (value === null || Number.isNaN(value)) delete next[key]
			else next[key] = value
			return next
		})
		setDirty(true)
	}

	// Aide de saisie : au blur d'une cellule J, pré-remplit la cellule N vide
	// de la même ligne à J + 25 % (arrondi au franc). Débrayable.
	const onCellBlur = (row, cat, moment) => {
		if (!autofillN || moment !== "J") return
		const jKey = cellKey(row.code, cat.zone, cat.categorie, "J")
		const nKey = cellKey(row.code, cat.zone, cat.categorie, "N")
		const j = draft[jKey]
		if (j != null && draft[nKey] == null && momentsOf(row).includes("N")) {
			setDraft((d) => ({ ...d, [nKey]: Math.round(j * 1.25) }))
			setDirty(true)
		}
	}

	const draftToCellules = () =>
		Object.entries(draft).map(([key, prix]) => {
			const [prestation, zone, categorie, moment] = key.split("|")
			return {
				prestation,
				zone,
				categorie: parseInt(categorie),
				moment,
				prix,
			}
		})

	const closeConfirm = () => {
		setConfirm(null)
		dispatch(resetPostGrillePolice())
	}

	const corpsSauver = () => ({
		action: "sauver",
		id: selected.id,
		valable_des: draftDate,
		cellules: draftToCellules(),
	})

	const doAction = () => {
		let action
		if (confirm === "sauver") {
			action = dispatch(
				postGrillePolice(corpsSauver(), "Brouillon enregistré"),
			)
		} else if (confirm === "vigueur") {
			// K10 : la saisie affichée est enregistrée PUIS mise en vigueur
			// (comme Tarifs privés) ; un refus du 1er arrête tout.
			// Passe 4 (🟡8) : saisie enregistrée → garde désarmée dès le
			// 1er succès, même si la mise en vigueur est refusée ensuite.
			action = dispatch(postGrillePolice(corpsSauver())).then((json) => {
				if (!json) return json
				setDirty(false)
				return dispatch(
					postGrillePolice(
						{ action: "mettre_en_vigueur", id: selected.id },
						"Grille mise en vigueur",
					),
				)
			})
		} else {
			action = dispatch(deleteGrillePolice(selected.id))
		}
		action.then((json) => {
			if (json) {
				setDirty(false)
				closeConfirm()
			}
		})
	}

	const creerBrouillon = () =>
		dispatch(
			postGrillePolice(
				{ action: "creer_brouillon" },
				"Brouillon créé depuis la version en vigueur",
			),
		).then((json) => {
			if (!json) return
			const nouveau = json.find((g) => g.etat === ETAT_BROUILLON)
			if (nouveau) {
				setSelectedId(nouveau.id)
				setDirty(false)
				setDraft(cellsFromGrille(nouveau))
				setDraftDate(nouveau.valable_des)
			}
		})

	if (fetchGrilles.loading && !grilles.length)
		return <Loader variant="ring" message="Chargement…" />

	const catsByZone = {
		normale: CATS.filter((c) => c.zone === "normale"),
		autoroute: CATS.filter((c) => c.zone === "autoroute"),
	}

	const confirmTitles = {
		sauver: "Enregistrer le brouillon ?",
		vigueur: "Mettre la grille en vigueur ?",
		supprimer: "Supprimer le brouillon ?",
	}

	return (
		<div style={styles.card}>
			{QuitGuard}
			{confirm && (
				<CustomModal
					title={confirmTitles[confirm]}
					onClose={closeConfirm}
					buttons={[
						{ title: "Annuler", onClick: closeConfirm },
						{
							title:
								confirm === "supprimer"
									? "Supprimer"
									: "Confirmer",
							styleName:
								confirm === "supprimer" ? "danger" : "primary",
							onClick: doAction,
							loading: postGrille.saving,
						},
					]}
				>
					{confirm === "sauver" && (
						<p>
							{diffKeys.length} cellule(s) diffèrent de la version
							en vigueur. Le brouillon reste modifiable.
						</p>
					)}
					{confirm === "vigueur" && (
						<p>
							{diffKeys.length} cellule(s) diffèrent de la version
							en vigueur. La nouvelle grille s'appliquera aux
							missions police clôturées dès le{" "}
							<b>{dateFr(draftDate)}</b> ; la version actuelle
							sera archivée. Les missions déjà clôturées ne sont
							pas recalculées.
						</p>
					)}
					{confirm === "supprimer" && (
						<p>
							Le brouillon et toutes ses cellules seront
							supprimés. Les versions en vigueur et archivées ne
							bougent pas.
						</p>
					)}
				</CustomModal>
			)}

			<div style={styles.head}>
				<div
					style={{
						display: "flex",
						alignItems: "flex-start",
						gap: 14,
					}}
				>
					<div>
						<div style={styles.title}>
							Tarifs officiels des dépannages — annexe (art. 2,
							al. 2)
						</div>
						<div style={styles.sub}>
							Grille appliquée par le serveur au calcul du prix à
							la clôture des missions <b>appel police</b>. Cellule
							vide = prestation non applicable. TVA incluse.
						</div>
					</div>
					{isBoss && !brouillon && (
						<button
							style={{ ...gs.btnSecondary, marginLeft: "auto" }}
							onClick={creerBrouillon}
						>
							+ Nouvelle version
						</button>
					)}
				</div>
			</div>

			<div style={styles.rail}>
				<span style={styles.railLabel}>Versions</span>
				{grilles.map((g) => {
					const isSelected = g.id === selectedId
					const isBrouillon = g.etat === ETAT_BROUILLON
					const dotColor =
						g.etat === ETAT_EN_VIGUEUR
							? COLORS.success
							: isBrouillon
								? COLORS.warning
								: COLORS.textLight
					const label =
						g.etat === ETAT_EN_VIGUEUR
							? `En vigueur depuis le ${dateFr(g.valable_des)}`
							: isBrouillon
								? "Brouillon · valable dès le"
								: `${dateFr(g.valable_des)} · archivée`
					return (
						<button
							key={g.id}
							style={styles.pill(isSelected, isBrouillon)}
							onClick={() => selectVersion(g)}
						>
							<span style={styles.dot(dotColor)} />
							{label}
							{isBrouillon && (
								<input
									type="date"
									style={styles.dateInput}
									value={
										isSelected ? draftDate : g.valable_des
									}
									disabled={!isSelected || !isBoss}
									onClick={(e) => e.stopPropagation()}
									onChange={(e) => {
										setDraftDate(e.target.value)
										setDirty(true)
									}}
								/>
							)}
						</button>
					)
				})}
			</div>

			<div style={styles.toolbar}>
				<span>
					<b>J</b> = 07h00 – 18h30 · <b>N</b> = 18h30 – 07h00,
					week-ends & fériés · cellule vide = <b>non applicable</b>
				</span>
				{editable ? (
					<label
						style={{
							marginLeft: "auto",
							display: "flex",
							alignItems: "center",
							gap: 7,
							cursor: "pointer",
						}}
					>
						<input
							type="checkbox"
							checked={autofillN}
							onChange={(e) => setAutofillN(e.target.checked)}
						/>
						Pré-remplir N = J + 25 %
					</label>
				) : (
					<span style={{ marginLeft: "auto" }}>
						🔒 Version figée — lecture seule
					</span>
				)}
			</div>

			<div style={styles.scroll}>
				<table style={styles.table}>
					<thead>
						<tr>
							<th
								style={{
									...styles.stickyCol,
									background: COLORS.bg,
									zIndex: 3,
								}}
								rowSpan={3}
							/>
							<th
								style={styles.zoneHead}
								colSpan={catsByZone.normale.length * 2}
							>
								Zone normale
							</th>
							<th
								style={{
									...styles.zoneHead,
									borderLeft: `2px solid ${COLORS.borderStrong}`,
								}}
								colSpan={catsByZone.autoroute.length * 2}
							>
								Autoroute
							</th>
						</tr>
						<tr>
							{CATS.map((c, i) => (
								<th
									key={`cat_${i}`}
									style={{
										...styles.catHead,
										borderLeft:
											i > 0 && c.zone !== CATS[i - 1].zone
												? `2px solid ${COLORS.borderStrong}`
												: undefined,
									}}
									colSpan={2}
								>
									{c.label}
								</th>
							))}
						</tr>
						<tr>
							{CATS.map((c, i) => (
								<React.Fragment key={`m_${i}`}>
									<th
										style={{
											...styles.momentHead,
											borderLeft:
												i > 0 &&
												c.zone !== CATS[i - 1].zone
													? `2px solid ${COLORS.borderStrong}`
													: undefined,
										}}
									>
										J
									</th>
									<th style={styles.momentHead}>N</th>
								</React.Fragment>
							))}
						</tr>
					</thead>
					<tbody>
						{SECTIONS.map((section) => (
							<React.Fragment key={section.titre}>
								<tr>
									<td
										style={{
											...styles.stickyCol,
											...styles.sectionRow,
										}}
									>
										{section.titre}
									</td>
									<td
										style={styles.sectionRow}
										colSpan={CATS.length * 2}
									/>
								</tr>
								{section.rows.map((row) => (
									<tr key={row.code}>
										<td style={styles.stickyCol}>
											<span style={styles.code}>
												{row.code}
											</span>
											<span style={styles.prestaLabel}>
												{row.label}
											</span>
											{row.note && (
												<span style={styles.prestaNote}>
													{row.note}
												</span>
											)}
										</td>
										{CATS.map((cat, i) => {
											const zoneStart =
												i > 0 &&
												cat.zone !== CATS[i - 1].zone
											const moments = momentsOf(row)
											const renderCell = (
												moment,
												colSpan,
												zs,
											) => {
												const applicable =
													moments.includes(moment)
												const k = cellKey(
													row.code,
													cat.zone,
													cat.categorie,
													moment,
												)
												const diff =
													editable &&
													diffKeys.includes(k)
												const value = shownCells[k]
												return (
													<td
														key={`${row.code}_${i}_${moment}`}
														style={styles.cell(
															diff,
															zs,
														)}
														colSpan={colSpan}
														title={
															diff
																? `En vigueur : ${
																		baseCells[
																			k
																		] !=
																		null
																			? `${baseCells[k]} CHF`
																			: "non applicable"
																	}`
																: undefined
														}
													>
														{!applicable ? (
															<span
																style={
																	styles.na
																}
															>
																—
															</span>
														) : editable ? (
															<input
																type="text"
																inputMode="numeric"
																style={styles.cellInput(
																	diff,
																)}
																value={
																	value ?? ""
																}
																placeholder="—"
																onChange={(e) =>
																	setCell(
																		k,
																		e.target
																			.value,
																	)
																}
																onBlur={() =>
																	onCellBlur(
																		row,
																		cat,
																		moment,
																	)
																}
															/>
														) : value != null ? (
															<span
																style={
																	styles.cellValue
																}
															>
																{value}
																{moment ===
																	"JOUR" &&
																	"/j"}
															</span>
														) : (
															<span
																style={
																	styles.na
																}
															>
																—
															</span>
														)}
													</td>
												)
											}
											if (row.moment === "JOUR")
												return renderCell(
													"JOUR",
													2,
													zoneStart,
												)
											return [
												renderCell("J", 1, zoneStart),
												renderCell("N", 1, false),
											]
										})}
									</tr>
								))}
							</React.Fragment>
						))}
					</tbody>
				</table>
			</div>

			<div style={styles.footnotes}>
				<span>
					* Tarif forfaitaire pour dépannages multiples pour les
					catégories concernées.
				</span>
				<span>
					** Véhicules à énergie alternative : majoration de 100 % des
					frais de garde.
				</span>
				<span>
					*** Supplément « véhicules &gt; 12 t » (S1) applicable aux
					positions 1 et 2 uniquement.
				</span>
			</div>

			{editable && (
				<div style={styles.savebar}>
					<span style={{ fontSize: 13, color: COLORS.text }}>
						<b style={{ color: COLORS.warning }}>
							{diffKeys.length}
						</b>{" "}
						cellule(s) modifiée(s) par rapport à la version en
						vigueur
					</span>
					<span style={{ flex: 1 }} />
					<button
						style={gs.btnSecondary}
						onClick={() => setConfirm("supprimer")}
					>
						Supprimer le brouillon
					</button>
					<button
						style={gs.btnSecondary}
						onClick={() => setConfirm("sauver")}
					>
						Enregistrer le brouillon
					</button>
					<button
						style={gs.btnPrimary}
						onClick={() => setConfirm("vigueur")}
					>
						Mettre en vigueur…
					</button>
				</div>
			)}
		</div>
	)
}

export default TarifsPoliceV2
